import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Search, Shuffle, LayoutGrid, List as ListIcon, Loader2, ArrowUpDown, ChevronRight } from "lucide-react";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { Filters } from "../components/Filters";
import { FruitCard, FruitRow } from "../components/FruitCard";
import { fetchFruits, fetchMeta, fetchStats, fetchRandom } from "../lib/api";

const FILTER_KEYS = ["canonicita", "categoria", "sottocategoria", "origine", "fonte",
  "risveglio_confermato", "possessore_noto", "con_immagine"];
const CAT_STATE = "fruttipedia_catalog_state";
const STEP = 12;

const SORTS = [
  { key: "nome-az", label: "Nome A–Z" },
  { key: "nome-za", label: "Nome Z–A" },
  { key: "categoria", label: "Categoria" },
  { key: "possessore", label: "Possessore" },
];

const toApiParams = (sp, limit) => {
  const p = { sort: sp.get("sort") || "nome-az", page: 1, page_size: limit };
  if (sp.get("q")) p.q = sp.get("q");
  if (sp.get("canonicita")) p.canonicita = sp.get("canonicita");
  if (sp.get("categoria")) p.categoria = sp.get("categoria");
  if (sp.get("sottocategoria")) p.sottocategoria = sp.get("sottocategoria");
  if (sp.get("origine")) p.origine = sp.get("origine");
  if (sp.get("fonte")) p.fonte = sp.get("fonte");
  if (sp.get("risveglio_confermato") === "1") p.risveglio_confermato = true;
  if (sp.get("possessore_noto") === "1") p.possessore_noto = true;
  if (sp.get("possessore_noto") === "0") p.possessore_noto = false;
  if (sp.get("con_immagine") === "1") p.con_immagine = true;
  return p;
};

const readState = () => { try { return JSON.parse(sessionStorage.getItem(CAT_STATE)); } catch { return null; } };

export default function Catalog() {
  const [sp, setSp] = useSearchParams();
  const navigate = useNavigate();
  const spKey = sp.toString();

  const saved = readState();
  const [limit, setLimit] = useState(saved && saved.key === spKey ? saved.limit : STEP);
  const [searchText, setSearchText] = useState(sp.get("q") || "");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [randomLoading, setRandomLoading] = useState(false);
  const restored = useRef(false);

  const view = sp.get("view") || "grid";
  const sort = sp.get("sort") || "nome-az";

  useEffect(() => {
    document.title = "Tutti i frutti | Fruttipedia";
  }, []);

  // debounce ricerca -> URL
  useEffect(() => {
    const t = setTimeout(() => {
      const next = new URLSearchParams(sp);
      if (searchText) next.set("q", searchText); else next.delete("q");
      if (next.toString() !== sp.toString()) { setSp(next, { replace: true }); setLimit(STEP); }
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText]);

  const { data: meta } = useQuery({ queryKey: ["meta"], queryFn: fetchMeta });
  const { data: stats } = useQuery({ queryKey: ["stats"], queryFn: fetchStats });

  const apiParams = useMemo(() => toApiParams(sp, limit), [sp, limit]);
  const { data, isFetching } = useQuery({
    queryKey: ["fruits", apiParams], queryFn: () => fetchFruits(apiParams), keepPreviousData: true,
  });

  // salva stato (chiave filtri + limit + scroll) per il ritorno dalla scheda
  useEffect(() => {
    const onScroll = () => {
      sessionStorage.setItem(CAT_STATE, JSON.stringify({ key: sp.toString(), limit, scrollY: window.scrollY }));
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [sp, limit]);

  // ripristina scroll al rientro
  useEffect(() => {
    if (restored.current || !data) return;
    const s = readState();
    if (s && s.key === sp.toString()) {
      requestAnimationFrame(() => window.scrollTo(0, s.scrollY || 0));
    }
    restored.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const setFilter = (key, value) => {
    const next = new URLSearchParams(sp);
    if (value) next.set(key, value); else next.delete(key);
    setSp(next, { replace: true });
    setLimit(STEP);
  };

  const resetFilters = () => {
    const next = new URLSearchParams();
    if (sp.get("view")) next.set("view", sp.get("view"));
    if (sp.get("sort")) next.set("sort", sp.get("sort"));
    setSp(next, { replace: true });
    setSearchText("");
    setLimit(STEP);
  };

  const activeCount = FILTER_KEYS.filter((k) => sp.get(k)).length + (sp.get("q") ? 1 : 0);
  const items = data?.items || [];
  const total = data?.total || 0;

  const onRandom = async () => {
    setRandomLoading(true);
    try { const f = await fetchRandom(); navigate(`/frutto/${f.slug}`); }
    finally { setRandomLoading(false); }
  };

  return (
    <div className="App">
      <Header />

      <main className="w-[min(1160px,calc(100%-32px))] mx-auto pt-5">
        <nav className="text-sm luma-muted flex items-center gap-1.5 mb-3" data-testid="breadcrumb">
          <Link to="/" className="hover:underline">Fruttipedia</Link>
          <ChevronRight size={14} />
          <span className="luma-ink font-semibold">Tutti i frutti</span>
        </nav>

        <h1 className="font-display text-3xl sm:text-4xl luma-ink mb-4">Tutti i frutti</h1>

        {/* search + actions */}
        <div className="luma-card p-4 sm:p-5 mb-5">
          <div className="relative">
            <Search size={20} className="absolute left-4 top-1/2 -translate-y-1/2 luma-muted" />
            <input
              data-testid="search-input"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Cerca un frutto o un possessore"
              className="luma-input w-full pl-12 pr-4 py-3.5 text-base font-medium"
            />
          </div>

          <div className="flex flex-wrap items-center gap-2 mt-3">
            <button onClick={onRandom} disabled={randomLoading} data-testid="random-fruit-btn"
              className="pill-soft px-4 py-2.5 text-sm flex items-center gap-2">
              {randomLoading ? <Loader2 size={16} className="animate-spin" /> : <Shuffle size={16} />}
              Frutto casuale
            </button>

            <div className="flex items-center gap-2 ml-auto">
              <div className="relative flex items-center">
                <ArrowUpDown size={15} className="absolute left-3 luma-muted pointer-events-none" />
                <select data-testid="sort-select" value={sort}
                  onChange={(e) => setFilter("sort", e.target.value === "nome-az" ? "" : e.target.value)}
                  className="luma-input pl-9 pr-8 py-2.5 text-sm font-bold appearance-none cursor-pointer">
                  {SORTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                </select>
              </div>

              <div className="pill-soft flex items-center p-1" style={{ minHeight: "auto" }}>
                <button data-testid="view-grid-btn" onClick={() => setFilter("view", "")} aria-label="Griglia"
                  className={`w-8 h-8 rounded-lg flex items-center justify-center ${view === "grid" ? "bg-[#F5EEFF]" : ""}`}
                  style={view === "grid" ? { color: "var(--luma-action)" } : {}}>
                  <LayoutGrid size={16} />
                </button>
                <button data-testid="view-list-btn" onClick={() => setFilter("view", "list")} aria-label="Elenco"
                  className={`w-8 h-8 rounded-lg flex items-center justify-center ${view === "list" ? "bg-[#F5EEFF]" : ""}`}
                  style={view === "list" ? { color: "var(--luma-action)" } : {}}>
                  <ListIcon size={16} />
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-[280px_1fr] gap-5 items-start">
          <div className="lg:sticky lg:top-24">
            <Filters meta={meta} filters={Object.fromEntries(sp)} setFilter={setFilter}
              reset={resetFilters} open={filtersOpen || (typeof window !== "undefined" && window.innerWidth >= 1024)}
              setOpen={setFiltersOpen} activeCount={FILTER_KEYS.filter((k) => sp.get(k)).length} />
          </div>

          <div>
            <div className="flex items-center justify-between mb-3 px-1">
              <p className="text-sm font-bold luma-muted" data-testid="results-count">
                {total} {total === 1 ? "frutto" : "frutti"}
                {stats && <span className="hidden sm:inline"> · {stats.totale} in archivio</span>}
              </p>
              {isFetching && <Loader2 size={16} className="animate-spin luma-muted" />}
            </div>

            {items.length === 0 && !isFetching ? (
              <div className="luma-card p-10 text-center" data-testid="empty-state">
                <p className="font-display text-lg luma-ink">Nessun frutto trovato</p>
                <p className="text-sm luma-muted mt-1">Prova a modificare la ricerca o ad azzerare i filtri.</p>
                {activeCount > 0 && (
                  <button onClick={resetFilters} className="pill-soft px-5 py-2.5 text-sm mt-4" data-testid="empty-reset">
                    Azzera tutto
                  </button>
                )}
              </div>
            ) : view === "list" ? (
              <div className="flex flex-col gap-2" data-testid="catalog-list">
                {items.map((f) => <FruitRow key={f.slug} fruit={f} />)}
              </div>
            ) : (
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4" data-testid="catalog-grid">
                {items.map((f) => <FruitCard key={f.slug} fruit={f} />)}
              </div>
            )}

            {items.length < total && (
              <div className="flex justify-center mt-7">
                <button onClick={() => setLimit((l) => l + STEP)} data-testid="load-more-btn"
                  className="pill-soft px-6 py-3 text-sm flex items-center gap-2">
                  Mostra altri ({total - items.length})
                </button>
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
